import InfiniteScroll from "react-infinite-scroll-component";
import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { chatRoomParticipantApi } from "@/react/api";
import { useProfileDrawer } from "@/react/components/Drawers/ProfileDrawer";
import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";
import { Typography } from "@circle-react/components/shared/uikit/Typography";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { useChatParticipantsApi } from "@circle-react/hooks/chatsV2";
import { Loader } from "@circle-react-shared/uikit/Loader";
import { useToast } from "@circle-react-uikit/ToastV2";
import { ParticipantList } from "./ParticipantList";
import { mapParticipantsPerStatus, sortParticipants } from "./helpers";

export interface MemberListProps {
  chatRoom: any;
  refetchChatRoom: () => void;
}

export const MemberList = ({ chatRoom, refetchChatRoom }: MemberListProps) => {
  const toastr = useToast();
  const memberProfileDrawer = useProfileDrawer();

  const { uuid, current_participant: currentParticipant } = chatRoom;

  const {
    chatParticipants = [],
    refetchChatParticipants,
    totalCount: totalParticipantsCount,
    hasNextPage,
    fetchNextPage,
  } = useChatParticipantsApi(uuid);

  const participants = [...sortParticipants(chatParticipants)];
  const { mutateAsync: handleMakeAdmin } = useMutation<any, ApiError, any>(
    ({ participantId, status }) =>
      chatRoomParticipantApi.update({
        uuid,
        id: participantId,
        admin: status,
      }),
    {
      onSuccess: ({
        chat_room_participant: {
          name,
          roles: { admin },
        },
      }) => {
        const displayName = name || t("messaging.member"); //Fallback incase name not is set.
        refetchChatParticipants();
        refetchChatRoom();
        toastr.success(
          admin
            ? t("messaging.member_made_admin", { name: displayName })
            : t("messaging.member_removed_as_admin", {
                name: displayName,
              }),
          {
            duration: "short",
            shouldUseProgress: false,
          },
        );
      },
      onError: () => {
        toastr.error(t("request_failure_message"));
      },
    },
  );

  const { mutateAsync: removeParticipant } = useMutation<any, ApiError, any>(
    ({ participantId }) =>
      chatRoomParticipantApi.destroy({
        uuid,
        id: participantId,
      }),
    {
      onSuccess: ({ message }) => {
        refetchChatParticipants();
        refetchChatRoom();
        toastr.success(message);
      },
      onError: () => {
        toastr.error(t("request_failure_message"));
      },
    },
  );

  const showMemberProfile = (memberPublicId: string) => {
    if (memberPublicId) {
      void memberProfileDrawer.show({
        memberPublicId,
      });
    }
  };

  const getProfileActionItems = (participant: ChatRoomParticipant) => [
    {
      value: "view-profile",
      icon: "20-eye-open",
      iconSize: 20,
      className: "!py-2.5 !px-3",
      label: t("view_profile"),
      onClick: () => showMemberProfile(participant.user_public_uid || ""),
    },
    {
      value: participant.admin ? "remove-admin" : "make-admin",
      label: participant.admin
        ? t("messaging.remove_admin")
        : t("messaging.make_admin"),
      icon: "20-moderator",
      iconSize: 20,
      className: "!py-2.5 !px-3",
      onClick: () =>
        handleMakeAdmin({
          participantId: participant.id,
          status: !participant.admin,
        }),
    },
    {
      value: "remove",
      label: t("messaging.remove"),
      icon: "20-close",
      iconSize: 20,
      className: "!py-2.5 !px-3",
      iconClassName: "text-feedback-error",
      color: "text-feedback-error",
      onClick: () => removeParticipant({ participantId: participant.id }),
    },
  ];

  const { onlineParticipants, offlineParticipants } =
    mapParticipantsPerStatus(participants);

  return (
    <div
      id="scrollableDiv"
      className="messaging-rail__content h-full overflow-auto"
    >
      <InfiniteScroll
        scrollThreshold={0.8}
        next={fetchNextPage}
        hasMore={Boolean(hasNextPage)}
        dataLength={totalParticipantsCount}
        className="px-6 pb-6"
        scrollableTarget="scrollableDiv"
        loader={<Loader center />}
      >
        {onlineParticipants.length > 0 && (
          <>
            <div className="text-default mb-2">
              <Typography.LabelXxs weight="bold" color="text-default">
                {t("chat_space.online")}
              </Typography.LabelXxs>
            </div>
            <ParticipantList
              currentParticipant={currentParticipant}
              getProfileActionItems={getProfileActionItems}
              participantList={onlineParticipants}
              showMemberProfile={showMemberProfile}
            />
          </>
        )}
        {offlineParticipants.length > 0 && (
          <>
            <div className="text-default my-2">
              <Typography.LabelXxs weight="bold" color="text-default">
                {t("chat_space.offline")}
              </Typography.LabelXxs>
            </div>
            <ParticipantList
              currentParticipant={currentParticipant}
              getProfileActionItems={getProfileActionItems}
              participantList={offlineParticipants}
              showMemberProfile={showMemberProfile}
            />
          </>
        )}
      </InfiniteScroll>
    </div>
  );
};
